import styled from 'styled-components';
import IconButton from '@mui/material/IconButton';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { styled as muiStyled } from '@mui/system';
import { useAppSelector, useAppDispatch } from '../hooks/useRedux';
import Swal from 'sweetalert2';
import { addCart } from '../store/authSlice';
import { useNavigate } from 'react-router-dom';

interface GoodsCardProps {
	id: number;
	img: string;
	title: string;
	artist: string;
	price: number;
}

const Container = styled.div`
	width: 22rem;
	margin: 3rem auto;
	cursor: pointer;
`;

const ImgWrapper = styled.div`
	width: 100%;
	height: 25rem;
	margin: 0 auto;
	position: relative;
	text-align: center;

	img {
		border-radius: 4px;
		width: 100%;
		height: 100%;
		margin: auto;
	}
`;

const Img = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

const Title = styled.div`
	margin: 1rem 0 0.6rem;
	font-size: 1.3rem;
	font-weight: 500;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const Artist = styled.div`
	margin: 0.7rem 0;
`;

const Price = styled.div`
	margin: 1rem 0;
	font-size: 1.2rem;

	strong {
		font-weight: 500;
	}
`;

const CustomButton = muiStyled(IconButton)({
	position: 'absolute',
	bottom: 12,
	right: 12,
	backgroundColor: '#0E1B4E',
	opacity: 0.7,

	'&:hover': {
		backgroundColor: '#0E1B4E',
		transform: 'scale(1.06)',
		transition: '0.3s',
		opacity: 1,
	},
});

const CustomCartIcon = muiStyled(AddShoppingCartIcon)({
	fontSize: '1.8rem',
	color: '#fff',
	padding: 4,
});

function GoodsCard({ id, img, title, artist, price }: GoodsCardProps): React.ReactElement {
	const isLogin = useAppSelector((state) => state.auth.isLogin);
	const user = useAppSelector((state) => state.auth.user);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const handlePrice = (number: number) => {
		let price = number.toLocaleString();
		return price;
	};

	const handleCartButton = (
		isLogin: boolean,
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>
	) => {
		if (!isLogin) {
			Swal.fire({
				title: '로그인 후 장바구니 이용 가능합니다.',
				confirmButtonText: '확인',
				confirmButtonColor: '#0E1B4E',
				customClass: {
					popup: 'swal-popup',
					title: 'swal-title',
					confirmButton: 'swal-confirm-button',
				},
				scrollbarPadding: false,
			});
		} else {
			const isExist = user.cart?.some((list) => {
				return list.productId === id;
			});

			if (isExist) {
				Swal.fire({
					title: '이미 장바구니에 담긴 상품입니다.',
					confirmButtonText: '확인',
					confirmButtonColor: '#0E1B4E',
					customClass: {
						popup: 'swal-popup',
						title: 'swal-title',
						confirmButton: 'swal-confirm-button',
					},
					scrollbarPadding: false,
				});
			} else {
				dispatch(addCart(id));

				Swal.fire({
					title: '장바구니에 정상적으로 추가되었습니다.',
					confirmButtonText: '장바구니로 이동',
					confirmButtonColor: '#0E1B4E',
					showCancelButton: true,
					cancelButtonText: '쇼핑 계속하기',
					cancelButtonColor: '#0E1B4E',
					focusCancel: true,
					customClass: {
						popup: 'swal-popup',
						title: 'swal-title',
						confirmButton: 'swal-confirm-button',
						cancelButton: 'swal-cancel-button',
					},

					scrollbarPadding: false,
				}).then((result) => {
					if (result.isConfirmed) {
						navigate('/cart');
					}
				});
			}
		}
	};

	return (
		<Container>
			<ImgWrapper>
				<Img src={img} alt={`${title}'s goods image`} />
				<CustomButton
					color='primary'
					aria-label='add to shopping cart'
					onClick={(e) => handleCartButton(isLogin, e)}
				>
					<CustomCartIcon />
				</CustomButton>
			</ImgWrapper>

			<Title>{title}</Title>
			<Artist>{artist}</Artist>
			<Price>
				<strong>{handlePrice(price)}</strong> 원
			</Price>
		</Container>
	);
}

export default GoodsCard;
